'use client';

import { ReactNode, memo } from 'react';
import clsx from 'clsx';
import { ComponentMemo } from '@headless-workspace/typings';
import { GlowError } from '../../molecules';
import { PropsWithClassName, PropsWithTestId } from '../../props';
import { GlowTextBodySmall, GlowTextCaption } from '../typography';

export type FieldState = 'error' | 'default';

export type GlowFieldWrapperProps = FieldProps &
    PropsWithTestId &
    PropsWithClassName & {
        required?: boolean;
        children: (props: {
            state: FieldState;
            helperTextId?: string;
            errorTextId?: string;
            maxLength?: number;
            required?: boolean;
        }) => ReactNode;
        value?: string | number | readonly string[];
        fieldId: string;
    };

export type FieldProps = {
    errorText?: string;
    helperText?: string;
    maxLength?: number;
    label?: ReactNode;
    optionalText?: string;
    forceState?: FieldState;
    displayMaxLength?: boolean;
};

export const GlowFieldWrapper: ComponentMemo<GlowFieldWrapperProps> = memo(
    ({
        className,
        children,
        errorText,
        helperText,
        label,
        maxLength,
        fieldId,
        required = false,
        optionalText,
        forceState,
        displayMaxLength = true,
        value,
    }) => {
        const valueLength = typeof value === 'string' ? value.length : 0;
        const helperTextId = helperText ? `${fieldId}-helper-text` : '';
        const errorTextId = errorText ? `${fieldId}-error-text` : '';

        const getState = () => {
            if (forceState) {
                return forceState;
            }
            if (errorText) {
                return 'error';
            }
            return 'default';
        };

        return (
            <div className={clsx('grid gap-0.5', className)}>
                {label && (
                    <div className={'flex justify-between items-end gap-0.5'}>
                        <p className={'flex gap-0.5'}>
                            <GlowTextBodySmall
                                htmlFor={fieldId}
                                TagName={'label'}
                                text={label}
                                className={'line-clamp-2'}
                            />
                            {!required && optionalText && (
                                <GlowTextBodySmall
                                    TagName={'span'}
                                    text={`(${optionalText})`}
                                    color={'tertiary'}
                                    className={'h-full'}
                                />
                            )}
                        </p>

                        {displayMaxLength && maxLength && (
                            <GlowTextBodySmall className={'ml-auto text-right'} text={`${valueLength}/${maxLength}`} />
                        )}
                    </div>
                )}
                {children({
                    state: getState(),
                    helperTextId,
                    errorTextId,
                    maxLength,
                    required,
                })}
                {helperText && <GlowTextCaption id={helperTextId} text={helperText} />}
                <GlowError text={errorText} />
            </div>
        );
    },
);

GlowFieldWrapper.displayName = 'GlowFieldWrapper';
